import React from 'react'
import PropTypes from 'prop-types'

import L from 'leaflet'
import { MapLayer } from 'react-leaflet'
import _ from 'lodash'

import Popup from './popup'

import './super-marker-cluster.css'

require('leaflet.markercluster')

// Wrapper for MarkerClusterGroup fra "leaflet.markercluster". Må ligge inni et Map eller MapLayer 
// fra "react-leaflet" for å få riktig context.
//
// Alle options til MarkerClusterGroup kan gis som properties til denne komponenten, men vil kun settes
// ved mount og blir ikke oppdatert dynamisk.
//
// Dette laget oppretter en L.Marker for hvert element i "data".
// Hvis "markerPopup" er satt vil laget, ved klikk på en marker, rendre en Popup ("react-leaflet") 
// med "markerPopup" som innhold og med "data"-property satt til data-objektet som tilhører marker-en 
// det ble klikket på. 
export default class SuperMarkerCluster extends MapLayer {
    constructor() {
        super()
        
        this.state = {
            popupObject: null
        }

        this._clicked = this._clicked.bind(this)
        this._popupClosed = this._popupClosed.bind(this)
    }

    getChildContext () {
        return {
            layerContainer: this.leafletElement,
        }
    }

    createLeafletElement () {
        let options = _.extend({}, this.props)
        if (!options.iconCreateFunction && options.clusterIcon) {
            let icon = options.clusterIcon
            options.iconCreateFunction = () => icon
        } else if (!options.iconCreateFunction && !options.clusterIcon) {
            options.iconCreateFunction = cluster => 
                L.divIcon({ 
                    html: '<svg height="40px" width="40px" viewBox="0 0 100 100"><g><circle cx="50%" cy="50%" r="45%" stroke="#52A4A7" stroke-width="8px" fill="white"></circle><text x="50%" y="50%" text-anchor="middle" stroke="black" dy="12" font-size="36">' + cluster.getChildCount() + '</text></g></svg>', 
                    iconSize: [40, 40],
                    className: 'vm-cluster-icon' 
                });
            
        }
        this.leafletElement = L.markerClusterGroup(options);
        return this.leafletElement;
    }

    componentWillMount() {
        super.componentWillMount()
    }

    componentDidMount() {
        super.componentDidMount()

        this._addMarkers(this.props)
        this.leafletElement.on('click', this._clicked)
        this.context.map.on('popupclose', this._popupClosed)
    }

    componentWillUnmount() {
        super.componentWillUnmount()
        this.leafletElement.off('click', this._clicked)
        this.context.map.off('popupclose', this._popupClosed)
    }

    componentDidUpdate(prevProps, prevState) {   
        // Alle markers fjernes fra laget og legges til på nytt. Dette er ganske raskt når addLayers() brukes
        // for å legge til alle i en operasjon. Hvis vi får ytelsesproblemer kan vi prøve med å oppdatere
        // lag-listen i stedet med individuelle removeLayer og addLayer. Kanskje vi kan bruke noen grove estimat
        // for å vurdere hva som er raskest, feks. antall dataobjekter i listen. 
        this.leafletElement.clearLayers()       
        this._addMarkers(this.props)

        // fjern popup hvis objektet popup-en viser ikke lenger finnes i data-listen
        if (this.state.popupObject && !_.includes(this.props.data, this.state.popupObject))
            this.setState({popupObject: null})
    }

    _addMarkers(props) {
        if (!props.data || props.data.length === 0) return

        let options = {
            interactive: Boolean(props.markerPopup)
        }
        if (props.markerIcon) 
            options.icon = props.markerIcon
        options.radius = 8
        options.color = '#52A4A7'
        options.fillColor = '#52A4A7'
        options.fillOpacity = 0.5
        options.weight = '4'

        if (props.markerOptions)
            options = {...options, ...props.markerOptions}

        let markers = props.data.map((obj) => { 
            // let marker = L.marker(L.latLng(obj.Latitude, obj.Longitude), options)
            let marker = L.circleMarker(L.latLng(obj.Latitude, obj.Longitude), options)
            marker._popupData = obj
            return marker
        })

        this.leafletElement.addLayers(markers)
    }

    render() {
        if (!this.state.popupObject) 
            return null

        return (
            <Popup position={[this.state.popupObject.Latitude, this.state.popupObject.Longitude]}>
                <this.props.markerPopup data={this.state.popupObject} />
            </Popup>
        )
    }  
    
    _clicked(e) {
        if (!this.props.markerPopup || !this.context.map || !e.layer._popupData) return
        this.setState({popupObject: e.layer._popupData})
        L.DomEvent.stopPropagation(e.originalEvent)
    }

    _popupClosed() {
        this.setState({popupObject: null})
    }
}

SuperMarkerCluster.contextTypes = {
    layerContainer: PropTypes.shape({
        addLayer: PropTypes.func.isRequired,
        removeLayer: PropTypes.func.isRequired,
    }),
    map: PropTypes.instanceOf(L.Map)
}

SuperMarkerCluster.childContextTypes = {
    layerContainer: PropTypes.shape({
        addLayer: PropTypes.func.isRequired,
        removeLayer: PropTypes.func.isRequired,
    })
}

SuperMarkerCluster.propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({
        Latitude: PropTypes.number,
        Longitude: PropTypes.number
    })),
    clusterIcon: PropTypes.instanceOf(L.Icon),
    markerIcon: PropTypes.instanceOf(L.Icon),
    markerPopup: PropTypes.any,

    // MarkerClusterGroup options
    showCoverageOnHover: PropTypes.bool,
    zoomToBoundsOnClick: PropTypes.bool,
    spiderfyOnMaxZoom: PropTypes.bool,
    removeOutsideVisibleBounds: PropTypes.bool,
    animate: PropTypes.bool,
    animateAddingMarkers: PropTypes.bool,
    disableClusteringAtZoom: PropTypes.number,
    maxClusterRadius: PropTypes.number,
    polygonOptions: PropTypes.object,
    singleMarkerMode: PropTypes.bool,
    spiderLegPolylineOptions: PropTypes.object,
    spiderfyDistanceMultiplier: PropTypes.number,
    iconCreateFunction: PropTypes.func
}

SuperMarkerCluster.defaultProps = {
    showCoverageOnHover: true,
    zoomToBoundsOnClick: true,
    spiderfyOnMaxZoom: true,
    removeOutsideVisibleBounds: true,
    animate: true,
    animateAddingMarkers: false,
    disableClusteringAtZoom: null,
    maxClusterRadius: 80,
    polygonOptions: {},
    singleMarkerMode: false,
    spiderLegPolylineOptions: {},
    spiderfyDistanceMultiplier: 1,
    iconCreateFunction: null
}



// WEBPACK FOOTER //
// ./src/felles/Kart/super-marker-cluster.jsx